import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from '../../axiosBaseQuery';
import lubricantTypesApiParser from './lubricantTypesApiParser';

// ---------- Types ----------
export interface LubricantType {
  id: string | number;
  name: string;
  remarks: string | null;
}

export interface LubricantTypesListResponse {
  data: LubricantType[];
}

// ---------- API Slice ----------
const lubricantTypesApi = createApi({
  reducerPath: 'lubricantTypesApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['LubricantType'],
  endpoints: (builder) => ({
    getLubricantTypesList: builder.query<LubricantTypesListResponse, any>({
      query: (params) => ({
        url: '/lubricant/types',
        method: 'GET',
        params: {
          ...params,
        },
      }),
      transformResponse: (res: any): LubricantTypesListResponse => ({
        data: lubricantTypesApiParser.LubricantTypesListParser(res),
      }),
    }),

    createLubricantType: builder.mutation<any, { name: string; remarks: string }>({
      query: (payload) => ({
        url: '/lubricant/types',
        method: 'POST',
        data: payload,
      }),
      invalidatesTags: ['LubricantType'],
    }),

    updateLubricantType: builder.mutation<
      any,
      { id: number; payload: { name: string; remarks: string } }
    >({
      query: ({ id, payload }) => ({
        url: `/lubricant/types/${encodeURIComponent(id)}`,
        method: 'PUT',
        data: payload,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'LubricantType' as const, id: id.toString() },
        'LubricantType',
      ],
    }),

    deleteLubricantType: builder.mutation<any, number>({
      query: (id) => ({
        url: `/lubricant/types/${encodeURIComponent(id)}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['LubricantType'],
    }),
  }),
});

// ---------- Exports ----------
export const lubricantTypesReducer = lubricantTypesApi.reducer;
export const lubricantTypesMiddleware = lubricantTypesApi.middleware;
export const lubricantTypesReducerPath = lubricantTypesApi.reducerPath;

export const {
  useGetLubricantTypesListQuery,
  useCreateLubricantTypeMutation,
  useUpdateLubricantTypeMutation,
  useDeleteLubricantTypeMutation,
} = lubricantTypesApi;
